import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { AppConfigService } from 'app/core/config/app-config.service';

export interface ChatFileUploadResult {
  id: number;
  uuid: string;
  original_name: string;
  mime_type: string;
  file_size: number;
  is_image: boolean;
  url: string;
}

export interface PendingChatAttachment {
  file: File;
  previewUrl: string | null;
  uploading: boolean;
  uploaded: boolean;
  error: boolean;
  result: ChatFileUploadResult | null;
}

@Injectable({ providedIn: 'root' })
export class ChatFileUploadService {
  private get baseUrl() { return this.appConfig.get<string>('serverUrl', ''); }

  constructor(private http: HttpClient, private appConfig: AppConfigService) {}

  /**
   * Upload a file to the chat-file endpoint.
   * Returns an Observable of the upload result.
   */
  upload(file: File, companyId: number, studentId: number, token: string): Observable<{ status: string; data: ChatFileUploadResult }> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    formData.append('company_id', String(companyId));
    formData.append('student_id', String(studentId));

    const headers = new HttpHeaders({ Authorization: 'Bearer ' + token });

    return this.http.post<{ status: string; data: ChatFileUploadResult }>(
      `${this.baseUrl}/v1/chat-file`,
      formData,
      { headers }
    );
  }

  /**
   * Delete an orphaned (unsent) chat file by ID.
   */
  delete(fileId: number, token: string): Observable<void> {
    const headers = new HttpHeaders({ Authorization: 'Bearer ' + token });
    return this.http.delete<void>(`${this.baseUrl}/v1/chat-file/${fileId}`, { headers });
  }

  /**
   * Returns a served URL for a chat file, appending the JWT as a query param
   * so it can be used in <img src="...">.
   */
  serveUrl(fileId: number, token: string): string {
    return `${this.baseUrl}/v1/chat-file/${fileId}?token=${encodeURIComponent(token)}`;
  }

  /**
   * Build a local object-URL preview for a File before uploading.
   */
  buildPreview(file: File): string | null {
    if (file.type.startsWith('image/')) {
      return URL.createObjectURL(file);
    }
    return null;
  }

  /** Revoke an object URL created by buildPreview() to free memory. */
  revokePreview(url: string | null): void {
    if (url && url.startsWith('blob:')) {
      URL.revokeObjectURL(url);
    }
  }

  /** Format file size for display (e.g. "1.2 MB"). */
  formatSize(bytes: number): string {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  }
}
